import { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import { addToast } from '@/redux/slices/toast.slice';

import { useAppDispatch } from '@/redux';
import { contextTrad } from '@/utils/locales';
import { hasOnlyLatinChars } from '@/utils/formUtils';

import TextInput from '@/components/common/TextInput';
import CheckboxV2 from '@/components/common/CheckboxV2';
import GiftOption from '@/components/checkout/common/MessageEditor/GiftOption';
import Commands from '@/components/checkout/common/MessageEditor/Commands';
import Message from '@/components/checkout/common/MessageEditor/Message';

import styles from './MessageEditor.module.scss';

export const MessageEditor = ({
  t, id, readOnly, onChange, message, labels, gift, messageMaxLength, actAsCustomer,
  deliveryInstructionEmail, deliveryInstructionPhoneNumber
}) => {
  const dispatch = useAppDispatch();
  const [showMessage, setShowMessage] = useState(message?.length > 0);
  const [currentMessage, setCurrentMessage] = useState(message);
  const [currentGift, setCurrentGift] = useState(gift);
  const [title, checkboxLabel, textAreaLabel, textAreaPlaceholder] = labels;
  const [editMode, setEditMode] = useState(false);
  const [shippingEmail, setShippingEmail] = useState(deliveryInstructionEmail);
  const [shippingPhoneNumber, setShippingPhoneNumber] = useState(deliveryInstructionPhoneNumber);

  const msgClassName = useMemo(() => (
    `row ${styles['message-section']} ${showMessage ? styles.show : ''} ${editMode ? styles['edit-section'] : styles['show-section']}`
  ), [showMessage, editMode]);

  useEffect(() => {
    setShowMessage(message?.length > 0);
    setCurrentMessage(message);
    setCurrentGift(gift);
  }, [message, gift]);

  /**
   * Toggle message show mode.
   * Either show existing message or enter edit mode if message yet empty.
   */
  const onShowMessage = () => {
    const msgLength = currentMessage?.length ?? 0;
    if (showMessage) {
      if (msgLength > 0) {
        onChange();
      }
      setShowMessage(false);
      setEditMode(false);
    } else {
      setEditMode(msgLength === 0);
      setShowMessage(true);
    }
  };

  /**
   * Process modify message button click
   * @param {*} e The DOM event
   */
  const onModifyMessage = (e) => {
    e.preventDefault();
    setEditMode(true);
  };

  /**
   * Process delete message button click
   * @param {*} e The DOM event
   */
  const onDeleteMessage = (e) => {
    e.preventDefault();
    setShowMessage(false);
    onChange();
  };

  /**
   * Process save modifications button click
   */
  const onSaveModification = () => {
    const nMessage = normalizeText(currentMessage);
    const msgInLatin = hasOnlyLatinChars(nMessage);

    if (typeof msgInLatin === 'string') {
      dispatch(addToast({ msg: t(msgInLatin) }));
    } else {
      const nGift = currentGift && {
        from: normalizeText(currentGift.from),
        to: normalizeText(currentGift.to)
      };

      const onChangeArgs = actAsCustomer ? { message: nMessage, shippingEmail, shippingPhoneNumber } : nMessage;

      if (nMessage?.length === 0) {
        onChange(onChangeArgs);
        setCurrentMessage(undefined);
        setCurrentGift(gift ? {} : undefined);
      } else {
        onChange(onChangeArgs, nGift);
        setCurrentGift(nGift);
        setCurrentMessage(nMessage);
      }

      setEditMode(false);
      setShowMessage(nMessage?.length > 0);
    }
  };

  /**
   * Process cancel modifications button click
   */
  const onCancelModification = () => {
    setCurrentGift(gift);
    setCurrentMessage(message);
    setShowMessage(message?.length > 0);
    setEditMode(false);
  };

  /**
   * Sanitize message by removing unneeded spaces
   * @param {string} str The source message
   * @return {string} The normalized message
   */
  const normalizeText = (str) =>
    str?.replace(/[ ]{2,}/g, ' ').trim();

  return (
    <div className={styles['message-editor']} data-test-id="message-editor" id={`ti-${id}-container`}>
      {readOnly ? (
        <p className={styles.title} data-test-id="message-editor-title-ro">
          {contextTrad(t, title, 'readonly')}
        </p>
      ) : (
        <p className={styles.title} data-test-id="message-editor-title">
          {t(title)}
        </p>
      )}
      {!readOnly && (
        <CheckboxV2
          id={`cb-${id}`}
          label={t(checkboxLabel)}
          className={styles.checkbox}
          onChange={onShowMessage}
          checked={showMessage}
        />
      )}
      <div className={msgClassName} data-test-id="message-section">
        {currentGift && (
          <>
            <div className="col-md-12">
              <GiftOption
                t={t}
                name="from"
                editMode={editMode}
                value={currentGift.from}
                setValue={(text) => setCurrentGift({ ...currentGift, from: text })}
              />
            </div>
            <div className="col-md-12">
              <GiftOption
                t={t}
                name="to"
                editMode={editMode}
                value={currentGift.to}
                setValue={(text) => setCurrentGift({ ...currentGift, to: text })}
              />
            </div>
          </>
        )}
        <div className="col-md-12">
          {actAsCustomer && (
            <>
              <TextInput
                id="email"
                label={t('admin:checkout.cartReview.deliveryInstruction.email')}
                value={shippingEmail}
                onChange={setShippingEmail}
                readOnly={!editMode}
              />
              <TextInput
                id="phoneNumber"
                label={t('admin:checkout.cartReview.deliveryInstruction.phoneNumber')}
                value={shippingPhoneNumber}
                onChange={setShippingPhoneNumber}
                readOnly={!editMode}
              />
            </>
          )}
          {showMessage && (
            <Message
              t={t}
              editMode={editMode}
              textAreaLabel={textAreaLabel}
              textAreaPlaceholder={textAreaPlaceholder}
              message={currentMessage}
              setMessage={setCurrentMessage}
              messageMaxLength={messageMaxLength}
              onblur={onSaveModification}
            />
          )}
        </div>
        {!readOnly && (
          <div className="col-md-12">
            <Commands
              t={t}
              editMode={editMode}
              onSaveModification={onSaveModification}
              onCancelModification={onCancelModification}
              onModifyMessage={onModifyMessage}
              onDeleteMessage={onDeleteMessage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

MessageEditor.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  message: PropTypes.string,
  labels: PropTypes.array.isRequired,
  gift: PropTypes.shape({
    from: PropTypes.string,
    msgTo: PropTypes.string
  }),
  readOnly: PropTypes.bool,
  messageMaxLength: PropTypes.number,
  actAsCustomer: PropTypes.object,
  deliveryInstructionEmail: PropTypes.string,
  deliveryInstructionPhoneNumber: PropTypes.string
};

export default MessageEditor;
